<template>
  <b-modal size="lg" :visible="visible" no-close-on-backdrop no-close-on-esc centered>
    <div slot="modal-header" class="w-100">
      <h5>{{ title }}<button type="button" class="close" @click.prevent="closeModal()">
        <span aria-hidden="true">&times;</span></button>
      </h5>
    </div>
    <b-form>
      <div class="container-fluid visit-charge-body">
        <div class="row justify-content-center m-2">
          <div class="alert alert-danger col" role="alert" v-show="generalErrorMessage">
            <div>{{ generalErrorMessage }}</div>
          </div>
        </div>
        <div class="row">
          <div class="col-2"></div>
          <div class="col-8">
            <div class="text-center mb-3">
              <span class="available-funds">Episode Total Amount:</span><span class="available-funds-amount"> ${{ formatNumber(bundle.amount) }} </span>
            </div>
            <div class="row">
              <div class="col-4 px-0 text-right">
                <h6>Current Services:</h6>
              </div>
              <div class="col px-2">
                <div v-if="!isLoading && bundle.services.length === 0">
                  No services defined.
                </div>
                <div v-if="isLoading">
                  <b-skeleton width="55%"></b-skeleton>
                </div>
                <div v-for="service in bundle.services" :key="service.organizationBundleServiceId" class="current-service">
                  {{ service.serviceType.name + ' - $' + formatNumber(service.amount) }}
                  <font-awesome-icon @click="removeService(service)" class="mx-1" :icon="['far', 'trash-alt']"/>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div v-if ="bundle.services.length > 0" class="col-4 px-0 text-right">
                <h6>Current Services Total:</h6>
              </div>
              <div v-if ="bundle.services.length > 0" class="col px-2">
                <div class="current-service">${{formatNumber(servicesTotal)}}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <hr/>
          </div>
        </div>
        <div class="row">
          <div class="col-2">
          </div>
          <div class="col-8">
            <b-form-group :label-cols="4"
                          label="Covered Service"
                          :state="stateForField('bundleServiceTypeId')"
                          :invalid-feedback="errorForField('bundleServiceTypeId')"
                          label-class="text-right">
              <b-dropdown menu-class="dropdown-container" :text="serviceName"
                          :variant="hasError('bundleServiceTypeId') ? 'outline-danger' : 'outline-dark'">
                <b-dropdown-item v-for="serviceType in bundleServiceTypes" :key="serviceType.bundleServiceTypeId"
                                 @click.prevent="setServiceType(serviceType)">{{ serviceType.name }}
                </b-dropdown-item>
              </b-dropdown>
            </b-form-group>
            <b-form-group id="amountFieldGroup"
                          horizontal
                          :label-cols="4"
                          breakpoint="md"
                          :state="stateForField('amount')"
                          :invalid-feedback="errorForField('amount')"
                          label="Amount"
                          label-for="amountField"
                          label-class="text-right">
              <b-input-group prepend="$">
                <b-form-input id="amountField" v-model="service.amount"></b-form-input>
              </b-input-group>
            </b-form-group>
          </div>
        </div>
      </div>
    </b-form>
    <div slot="modal-footer" class="w-100">
      <button type="button" class="btn btn-orange float-right" @click.prevent="addService()"><font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner"  pulse/>Add Service</button>
      <button type="button" class="btn btn-outline-orange mr-2 float-right" @click.prevent="closeModal()">Close</button>
    </div>
  </b-modal>
</template>

<script>
  import api from '../services/api'
  import ErrorsMixin from '../mixins/ErrorsMixin.js'

  export default {
    name: 'BundleServicesModal',

    mixins: [ErrorsMixin],

    props: {
      visible: {
        type: Boolean,
        required: true
      },

      bundleId: {
        type: Number,
        required: true
      },
      title: {
        type: String,
        default: 'Episode Services'
      }
    },

    data () {
      return {
        service: {
          amount: '',
          bundleServiceTypeId: 0
        },
        bundle: {
          bundleId: 0,
          services: []
        },
        bundleServiceTypes: [],
        isLoading: true,
        isSaving: false,
        changed: false,
        serviceName: 'Click to select'
      }
    },

    watch: {
      visible (newValue, oldValue) {
        if (newValue) {
          this.clearData()
          this.loadServiceTypes()
          if (this.bundleId > 0) {
            this.loadBundle()
          }
        }
      }
    },

    computed: {
      servicesTotal () {
        let sum = 0
        this.bundle.services.forEach(service => {
          sum += service.amount
        })
        return sum
      }
    },

    methods: {
      clearData () {
        this.clearAllErrors()
        this.service = {
          amount: '',
          bundleServiceTypeId: 0
        }
        this.bundle = {
          bundleId: 0,
          services: []
        }
        this.changed = false
      },

      validate () {
        this.clearAllErrors()
        const amountValue = parseFloat(this.service.amount)

        if (!amountValue) {
          this.addErrorForField('amount', 'An amount is required.')
        }

        if (amountValue < 0) {
          this.addErrorForField('amount', 'Amount must be greater then zero.')
        }

        if (!this.service.bundleServiceTypeId) {
          this.addErrorForField('bundleServiceTypeId', 'You must select a service.')
        }

        return !this.hasErrors()
      },

      setServiceType (serviceType) {
        this.service.bundleServiceTypeId = serviceType.bundleServiceTypeId
        this.serviceName = serviceType.name
      },

      loadServiceTypes () {
        api.get('/Dashboard/BundleServiceTypes').then(response => {

          this.bundleServiceTypes = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load bundle service types: ' + error.message, error)
          }
        })
      },

      loadBundle () {
        this.isLoading = true
        api.get('/Dashboard/Bundle/' + this.bundleId).then(response => {

          this.bundle = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load bundle: ' + error.message, error)
          }
        }).then(() => {
          this.isLoading = false
        })
      },

      addService () {

        if (this.isSaving) {
          return
        }

        if (!this.validate()) {
          return
        }

        this.isSaving = true
        this.errorMessage = ''
        api.post('/Dashboard/Bundle/' + this.bundleId + '/Service', this.service).then(response => {

          const service = response.data
          this.bundle.services.push(service)
          this.changed = true

          this.$emit('serviceAdded', service)

        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to add bundle service: ' + error.message, error)
          }
        }).then(() => {
          this.isSaving = false
          this.service = {
            amount: '',
            bundleServiceTypeId: 0
          }
          this.serviceName = 'Click to select'
        })
      },

      removeService (service) {
        this.errorMessage = ''
        api.delete('/Dashboard/Bundle/Service/' + service.organizationBundleServiceId).then(response => {
          this.loadBundle()
          this.changed = true
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to remove service: ' + error.message, error)
          }
        })
      },

      closeModal () {
        this.$emit('closeModal', this.changed)
      },

      formatNumber (number) {
        if (number === 0 || number === 0.0) {
          return '0.00'
        }
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return ''
      }
    }
  }
</script>

<style scoped>
  .current-service .fa-trash-can {
    cursor: pointer;
  }

  .current-service .fa-trash-can:HOVER {
    color: rgb(255, 70, 0);
  }
  .available-funds {
    color: #2A84F4;
    font-weight: 500;
    font-size: 18px;
  }
  .available-funds-amount {
    font-weight: 500;
    font-size: 18px;
  }
</style>
